import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  nationalArchivesApi,
  type NationalArchivesRecordDetails,
} from "../api/nationalArchives";
import Header from "../components/Common/Header";

export default function NationalArchivesRecord() {
  const { recordId } = useParams<{ recordId: string }>();
  const { getAccessTokenSilently } = useAuth0();

  const [record, setRecord] = useState<NationalArchivesRecordDetails | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecord = async () => {
      if (!recordId) return;

      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const details = await nationalArchivesApi.getRecordDetails(
          recordId,
          token
        );
        setRecord(details);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load record details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRecord();
  }, [recordId, getAccessTokenSilently]);

  if (loading) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">
              Error Loading Record
            </h2>
            <p className="text-red-700">{error}</p>
            <Link
              to="/national-archives"
              className="inline-block mt-4 text-blue-600 hover:underline"
            >
              ← Back to Search
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <p className="text-gray-600">Record not found</p>
          <Link
            to="/national-archives"
            className="inline-block mt-4 text-blue-600 hover:underline"
          >
            ← Back to Search
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
      {/* Back link */}
      <Link
        to="/national-archives"
        className="inline-flex items-center text-blue-600 hover:underline mb-6"
      >
        ← Back to Search
      </Link>

      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {record.title || record.reference || "Untitled Record"}
        </h1>

        {record.reference && (
          <p className="text-lg text-gray-600 mb-4">
            Reference: <span className="font-mono">{record.reference}</span>
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          {record.digitised && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              ✓ Digitised
            </span>
          )}
          {record.source && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              Source: {record.source}
            </span>
          )}
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {record.description && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Description
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {record.description}
              </p>
            </div>
          )}

          {/* Administrative Background */}
          {record.administrativeBackground && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Administrative Background
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {record.administrativeBackground}
              </p>
            </div>
          )}

          {/* Arrangement */}
          {record.arrangement && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Arrangement
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {record.arrangement}
              </p>
            </div>
          )}

          {/* Custodial History */}
          {record.custodialHistory && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Custodial History
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {record.custodialHistory}
              </p>
            </div>
          )}

          {/* Related Material */}
          {record.relatedMaterial && record.relatedMaterial.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Related Material
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                {record.relatedMaterial.map((item, idx) => (
                  <li key={idx}>{JSON.stringify(item)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right column - Metadata */}
        <div className="space-y-6">
          {/* Key Details */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Key Details
            </h2>

            <dl className="space-y-4">
              {record.coveringDates && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Covering Dates
                  </dt>
                  <dd className="text-gray-900">{record.coveringDates}</dd>
                </div>
              )}

              {record.heldBy && record.heldBy.length > 0 && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Held by</dt>
                  <dd className="text-gray-900">
                    {record.heldBy.map((h, idx) => (
                      <div key={idx}>{h.name}</div>
                    ))}
                  </dd>
                </div>
              )}

              {record.language && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Language</dt>
                  <dd className="text-gray-900">{record.language}</dd>
                </div>
              )}

              {record.physicalDescription && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Physical Description
                  </dt>
                  <dd className="text-gray-900">{record.physicalDescription}</dd>
                </div>
              )}

              {record.closureStatus && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Closure Status
                  </dt>
                  <dd className="text-gray-900">{record.closureStatus}</dd>
                </div>
              )}

              {record.accessConditions && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Access Conditions
                  </dt>
                  <dd className="text-gray-900">{record.accessConditions}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* People */}
          {record.people && record.people.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                People
              </h2>
              <ul className="text-gray-700 space-y-1">
                {record.people.map((person, idx) => (
                  <li key={idx}>{JSON.stringify(person)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Places */}
          {record.places && record.places.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Places
              </h2>
              <ul className="text-gray-700 space-y-1">
                {record.places.map((place, idx) => (
                  <li key={idx}>{place}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Subjects */}
          {record.subjects && record.subjects.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Subjects
              </h2>
              <ul className="text-gray-700 space-y-1">
                {record.subjects.map((subject, idx) => (
                  <li key={idx}>{JSON.stringify(subject)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* External Links */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              External Links
            </h2>
            <div className="space-y-3">
              <a
                href={record.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full px-4 py-2 bg-blue-600 text-white text-center rounded hover:bg-blue-700 transition-colors"
              >
                View on National Archives →
              </a>
              {record.registryUrl && (
                <a
                  href={record.registryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-4 py-2 border border-gray-300 text-gray-700 text-center rounded hover:bg-gray-50 transition-colors"
                >
                  View in Original Registry
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
